import { MutationTree } from "vuex";
import { State } from "./state";
import { MutationType } from "./mutation-types";
import { LoggedInPlayer } from "../protocol/dto/logged-in-player";
import { RoomItem } from "../types/room-item";
import { AbstractPlayer } from "../types/abstract-player";
import { ChatMessage } from "../protocol/messages/chat-message";
import { store } from ".";
import { GameItem } from "../types/game-item";
import { Player } from "../game/player";
import { SettingsModel } from "../types/settings-model";
import { WebsocketMessageType } from "../protocol/websocket-message-type";

/**
 * This class defines the Vuex mutations.
 * 
 * This is used for chainging the state in the Vuex store.
 * If we update any changes in the store we can fire events.
 * This class also defines the mutation type.
 * 
 * Mutations are used for synchronous state management.
 */
export type Mutations = {
  // login and user
  [MutationType.AUTH_USER](state: State, userObj: LoggedInPlayer): void;
  [MutationType.ROOMS](state: State, activeRooms: { rooms: RoomItem[] }): void;
  [MutationType.SELECT_ROOM](state: State, selectedRoomId: string): void;
  [MutationType.SELECT_GAME_KIND](state: State, selectedGameKind: string): void;
  [MutationType.ENTER_ROOM](state: State, joinedActiveRoom: RoomItem): void;
  [MutationType.USER_CONNECTED](state: State, status: boolean): void;

  // main game windows
  [MutationType.LOGIN_SHOWN](state: State, value: boolean): void;
  [MutationType.ROOMS_SHOWN](state: State, status: boolean): void;
  
  // chat and room panel
  [MutationType.PLAYER_LIST](state: State, playerList: AbstractPlayer[]): void;
  [MutationType.USER_ADDED](state: State, player: AbstractPlayer): void;
  [MutationType.USER_REMOVED](state: State, player: AbstractPlayer): void;
  [MutationType.CHAT_MESSAGE](state: State, ChatMessage: ChatMessage): void;
  [MutationType.GAME_MESSAGE](state: State, GameMessage: string): void;
  [MutationType.CHAT_CLEAR](state: State): void;

  // new game invite modal
  [MutationType.INVITATION_REQUEST_MODAL](state: State, status: boolean): void;
  [MutationType.INVITATION_REQUEST_GAME](state: State, game: GameItem): void;
  [MutationType.INVITATION_REQUEST_TXT](state: State, text: string): void;

  // information modal
  [MutationType.INFO_MODAL](state: State, status: boolean): void;
  [MutationType.INFO_TXT](state: State, text: string): void;

  // wait for opponent modal
  [MutationType.WAIT_FOR_OPPONENT_MODAL](state: State, status: boolean): void;

  // join tournament modal
  [MutationType.JOIN_TOURNAMENT_MODAL](state: State, status: boolean): void;

  // game and player status
  [MutationType.PLAYER1](state: State, player: Player): void;
  [MutationType.PLAYER2](state: State, opponentPlayer: Player): void;
  [MutationType.PLAY_TURN](state: State, playTurn: string): void;

  // settings
  [MutationType.SETTINGS](state: State, settings: SettingsModel): void;
};

/**
 * This is to define mutation for updating Vuex store state.
 */
export const mutations: MutationTree<State> & Mutations = {
  // login and user
  [MutationType.AUTH_USER](state, user) {
    state.isLoginShown = false;
    state.isRoomsShown = false;
    state.user = user;
  },
  [MutationType.ROOMS](state, rooms) {
    state.rooms = rooms.rooms;
    state.isRoomsShown = true;
  },
  [MutationType.SELECT_ROOM](state, selectedRoomId) {
    state.selectedRoomId = selectedRoomId;
  },
  [MutationType.SELECT_GAME_KIND](state, selectedGameKind) {
    state.selectedGameKind = selectedGameKind;
  },
  [MutationType.ENTER_ROOM](state, room) {
    state.joinedRoom = room;
    state.isRoomsShown = false;
  },
  [MutationType.USER_CONNECTED](state, status) {
    state.userConnected = status;
  },

  // main game windows
  [MutationType.LOGIN_SHOWN](state, value) {
    state.isLoginShown = value;
  },
  [MutationType.ROOMS_SHOWN](state, status) {
    state.isRoomsShown = status;
  },

  // chat and room panel
  [MutationType.PLAYER_LIST](state, playerList) {
    state.playerList = playerList;
  },

  /**
   * This is envoked when a player is added to the room.
   * 
   * @param state
   * @param player - the player
   */
  [MutationType.USER_ADDED](state, player) {
    state.playerList.push(player);
  },

  /**
   * This is envoked when the player details are updated.
   * 
   * @param state
   * @param player - the player
   */
  [MutationType.USER_UPDATED](state, player) {
    const playerList: AbstractPlayer[] = store.state.playerList;
    const playerIndex: number = playerList.findIndex(
      (p) => p.playerId === player.playerId
    );
    playerList[playerIndex] = player;
  },

  /**
   * This is invoked when a player is removed from a room.
   * 
   * @param state
   * @param player - the player
   */
  [MutationType.USER_REMOVED](state, player) {
    const playerList: AbstractPlayer[] = store.state.playerList;
    const playerIndex: number = playerList.findIndex(
      (p) => p.playerId === player.playerId
    );
    if (playerIndex > -1) {
      state.playerList.splice(playerIndex, 1);
    }
    state.isRoomsShown = true;
  },

  /**
   * This is invoked when a chat message is to be displayed on the chat.
   * 
   * @param state 
   * @param chatMessage - the chat message
   */
  [MutationType.CHAT_MESSAGE](state, chatMessage) {
    state.chatMessages.push(chatMessage);

    // Scroll to the bottom of the chat messages.
    // The time out is necessary otherwise the element does not scroll
    // to the last element. I think this is because time is needed to 
    // display the element.
    setTimeout(() => {
      try {
        const messages = document.getElementById('chat-messages');
        //@ts-ignore
        messages.scrollTop = messages.scrollHeight;
      } catch (e) {
        // do nothing
      }
    }, 100);
  },

  /**
   * This is invoked when a game message is to be displayed on the chat.
   * 
   * @param state 
   * @param chatMessage - the game message
   */
  [MutationType.GAME_MESSAGE](state, gameMessage) {

    const message: ChatMessage = {
      msgType: WebsocketMessageType.GAME_MESSAGE,
      playerId: 'GAME',
      message: gameMessage,
    }

    state.chatMessages.push(message);

    // Scroll to the bottom of the chat messages.
    // The time out is necessary otherwise the element does not scroll
    // to the last element. I think this is because time is needed to 
    // display the element.
    setTimeout(() => {
      try {
        const messages = document.getElementById("chat-messages");
        //@ts-ignore
        messages.scrollTop = messages.scrollHeight;
      } catch (e) {
        // do nothing
      }
    }, 100);
  },

  /**
   * Clear the chat message panel.
   */
  [MutationType.CHAT_CLEAR](state) {
    state.chatMessages = [];
  },

  // invitation request modal
  [MutationType.INVITATION_REQUEST_MODAL](state, status) {
    state.isShowInvitationRequestModal = status;
  },
  [MutationType.INVITATION_REQUEST_GAME](state, game) {
    state.inviteGame = game;
  },
  [MutationType.INVITATION_REQUEST_TXT](state, text) {
    state.inviteText = text;
  },

  // information modal
  [MutationType.INFO_MODAL](state, status) {
    state.isShowInfoModal = status;
  },
  [MutationType.INFO_TXT](state, text) {
    state.infoText = text;
  },

  // wait for opponent modal
  [MutationType.WAIT_FOR_OPPONENT_MODAL](state, status) {
    state.isShowWaitForOpponentModal = status;
  },

  // join tournament modal
  [MutationType.JOIN_TOURNAMENT_MODAL](state, status) {
    state.isJoinTournamentModal = status;
  },
  [MutationType.JOIN_TOURNAMENT_ROOM](state, room) {
    state.tournamentRoom = room;
  },
  [MutationType.JOIN_TOURNAMENT_REG](state, isRegistered) {
    state.isRegistered = isRegistered;
  },

  // game and player status
  [MutationType.PLAYER1](state, player) {
    state.player1 = player;
  },
  [MutationType.PLAYER2](state, opponentPlayer) {
    state.player2 = opponentPlayer;
  },
  [MutationType.PLAY_TURN](state, playTurn) {
    state.playTurn = playTurn;
  },

  // settings
  [MutationType.SETTINGS](state, settings) {
    state.settings = settings;
  },
};
